import { useState, useRef, useEffect, useCallback, memo, useMemo } from "react";
import { Send, Smile, ChevronLeft, Video } from "lucide-react";
import { useMessages } from "@/hooks/useChat";
import { useAuthContext } from "@/contexts/AuthContext";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";
import { useMessageReactions } from "@/hooks/useMessageReactions";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useVideoCall } from "@/hooks/useVideoCall";
import { useCallRecording } from "@/hooks/useCallRecording";
import { supabase } from "@/integrations/supabase/client";
import TypingIndicator from "./TypingIndicator";
import ReadReceipt from "./ReadReceipt";
import MessageReactions from "./MessageReactions";
import ReactionPicker from "./ReactionPicker";
import OnlineIndicator from "./OnlineIndicator";
import VoiceRecorder from "./VoiceRecorder";
import VoiceMessagePlayer from "./VoiceMessagePlayer";
import ImagePicker from "./ImagePicker";
import GifPicker from "./GifPicker";
import ChatImage from "./ChatImage";
import VideoCallModal from "./VideoCallModal";
import IncomingCallModal from "./IncomingCallModal";
import VideoMessageRecorder from "./VideoMessageRecorder";
import VideoMessagePlayer from "./VideoMessagePlayer";

interface ChatModalProps {
  chatId: string;
  otherUser: {
    id: string;
    name: string;
    photo_url: string | null;
  };
  isOpen: boolean;
  onClose: () => void;
}

const EMOJI_LIST = ["😊", "😂", "❤️", "🔥", "👋", "😍", "🎈", "✨", "💕", "🥰", "😘", "🤗"];

const ChatModal = memo(({ chatId, otherUser, isOpen, onClose }: ChatModalProps) => {
  const { user } = useAuthContext();
  const { messages, loading, sendMessage, markAsRead, refetch } = useMessages(chatId);
  const { isOtherUserTyping, typingUserName, startTyping, stopTyping } = useTypingIndicator(chatId);
  const { toggleReaction, getGroupedReactions } = useMessageReactions(chatId);
  const { isOtherUserOnline, statusText } = useOnlineStatus(chatId, otherUser.id);
  const { 
    callState,
    peerConnection,
    startCall, 
    answerCall, 
    declineCall, 
    endCall, 
    toggleVideo, 
    toggleAudio,
    toggleScreenShare,
  } = useVideoCall(chatId, otherUser.id);
  const {
    isRecording,
    recordingDuration,
    hasConsent: hasRecordingConsent,
    recordedBlob,
    startRecording,
    stopRecording,
    grantConsent,
    denyConsent,
    resetRecording,
  } = useCallRecording();
  const [newMessage, setNewMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showConsentRequest, setShowConsentRequest] = useState(false);
  const [showVideoRecorder, setShowVideoRecorder] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if video exchange has occurred (both users sent at least one video)
  const videoExchangeComplete = useMemo(() => {
    if (!user || messages.length === 0) return false;
    
    const myVideoMessages = messages.filter(m => m.sender_id === user.id && (m as any).video_url);
    const theirVideoMessages = messages.filter(m => m.sender_id === otherUser.id && (m as any).video_url);
    
    // Unlock text/voice after both have sent video messages
    return myVideoMessages.length > 0 && theirVideoMessages.length > 0;
  }, [messages, user, otherUser.id]);

  // Check if I've sent my first video
  const mySentVideo = useMemo(() => {
    if (!user) return false;
    return messages.some(m => m.sender_id === user.id && (m as any).video_url);
  }, [messages, user]);

  // Handle recording consent
  const handleRequestConsent = useCallback(() => {
    setShowConsentRequest(true);
  }, []);

  const handleConsentAccepted = useCallback(() => {
    grantConsent();
    setShowConsentRequest(false);
    // Auto-start recording after consent
    if (callState.remoteStream && callState.localStream) {
      startRecording(callState.remoteStream, callState.localStream);
    }
  }, [grantConsent, startRecording, callState.remoteStream, callState.localStream]);

  const handleConsentDeclined = useCallback(() => {
    denyConsent();
    setShowConsentRequest(false);
  }, [denyConsent]);

  const handleStartRecording = useCallback(() => {
    if (callState.remoteStream && callState.localStream) {
      startRecording(callState.remoteStream, callState.localStream);
    }
  }, [startRecording, callState.remoteStream, callState.localStream]);

  // Reset recording when call ends
  useEffect(() => {
    if (!callState.isConnected && !callState.isCalling) {
      // Keep recordedBlob for download, but stop recording
      if (isRecording) {
        stopRecording();
      }
    }
  }, [callState.isConnected, callState.isCalling, isRecording, stopRecording]);

  // Handle reaction toggle
  const handleToggleReaction = useCallback(async (messageId: string, emoji: string) => {
    await toggleReaction(messageId, emoji);
  }, [toggleReaction]);

  // Handle sending GIF
  const handleSendGif = useCallback(async (gifUrl: string) => {
    if (!user) return;
    
    await supabase.from("messages").insert({
      chat_id: chatId,
      sender_id: user.id,
      content: "GIF",
      image_url: gifUrl,
    });
    refetch();
  }, [user, chatId, refetch]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Mark messages as read when opening chat
  useEffect(() => {
    if (isOpen && chatId) {
      markAsRead();
    }
  }, [isOpen, chatId, markAsRead]);

  const handleSend = useCallback(async () => {
    if (!newMessage.trim() || isSending) return;

    stopTyping();
    setIsSending(true);
    const { error } = await sendMessage(newMessage.trim());
    setIsSending(false);

    if (!error) {
      setNewMessage("");
      inputRef.current?.focus();
    }
  }, [newMessage, isSending, sendMessage, stopTyping]);

  // Handle input change with typing indicator
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    if (e.target.value.trim()) {
      startTyping();
    } else {
      stopTyping();
    }
  }, [startTyping, stopTyping]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const addEmoji = useCallback((emoji: string) => {
    setNewMessage((prev) => prev + emoji);
    setShowEmoji(false);
    inputRef.current?.focus();
  }, []);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-card flex flex-col animate-slide-up">
      {/* Header */}
      <header className="flex items-center gap-3 p-4 border-b border-border bg-card">
        <button onClick={onClose} className="p-2 -ml-2 hover:bg-muted rounded-full transition-colors">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <div className="relative w-10 h-10">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
            {otherUser.photo_url ? (
              <img src={otherUser.photo_url} alt={otherUser.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-lg font-bold text-muted-foreground">
                {otherUser.name?.[0]}
              </div>
            )}
          </div>
          <OnlineIndicator isOnline={isOtherUserOnline} size="md" />
        </div>
        <div className="flex-1">
          <h2 className="font-semibold">{otherUser.name}</h2>
          <p className="text-xs text-muted-foreground">
            {isOtherUserTyping ? "typing..." : statusText}
          </p>
        </div>
        <button
          onClick={startCall}
          className="p-2 hover:bg-muted rounded-full transition-colors"
          title="Start video call"
        >
          <Video className="w-5 h-5" />
        </button>
      </header>

      {/* Video Call Modals */}
      {callState.isIncoming && !callState.localStream && (
        <IncomingCallModal
          callerName={otherUser.name}
          callerPhoto={otherUser.photo_url}
          onAnswer={answerCall}
          onDecline={declineCall}
        />
      )}

      {(callState.isCalling || callState.isConnected || callState.localStream || callState.isReconnecting) && (
        <VideoCallModal
          localStream={callState.localStream}
          remoteStream={callState.remoteStream}
          isConnected={callState.isConnected}
          isCalling={callState.isCalling}
          isReconnecting={callState.isReconnecting}
          isScreenSharing={callState.isScreenSharing}
          isRecording={isRecording}
          recordingDuration={recordingDuration}
          hasRecordingConsent={hasRecordingConsent}
          showConsentRequest={showConsentRequest}
          recordedBlob={recordedBlob}
          otherUserName={otherUser.name}
          otherUserPhoto={otherUser.photo_url}
          peerConnection={peerConnection}
          onEndCall={endCall}
          onToggleVideo={toggleVideo}
          onToggleAudio={toggleAudio}
          onToggleScreenShare={toggleScreenShare}
          onStartRecording={handleStartRecording}
          onStopRecording={stopRecording}
          onRequestConsent={handleRequestConsent}
          onConsentAccepted={handleConsentAccepted}
          onConsentDeclined={handleConsentDeclined}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 rounded-full border-4 border-pop border-t-transparent animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Say hi with a video! 👋🎬</p>
            <button
              onClick={() => setShowVideoRecorder(true)}
              className="mt-4 px-6 py-3 rounded-full pop-gradient text-white font-medium inline-flex items-center gap-2"
            >
              <Video className="w-5 h-5" />
              Record Video Hello
            </button>
          </div>
        ) : (
          <>
            {messages.map((message, index) => {
              const isMe = message.sender_id === user?.id;
              const isLastFromMe = isMe && (
                index === messages.length - 1 || 
                messages[index + 1]?.sender_id !== user?.id
              );
              const reactions = getGroupedReactions(message.id);
              
              return (
                <div key={message.id} className={`group flex ${isMe ? "justify-end" : "justify-start"}`}>
                  <div className={`flex items-end gap-1 max-w-[75%] ${isMe ? "flex-row-reverse" : "flex-row"}`}>
                    <div>
                      <div
                        className={`px-4 py-2.5 rounded-2xl ${
                          isMe
                            ? "bg-pop text-white rounded-br-md"
                            : "bg-muted text-foreground rounded-bl-md"
                        }`}
                      >
                        {(message as any).video_url ? (
                          <VideoMessagePlayer 
                            videoUrl={(message as any).video_url} 
                            isOwnMessage={isMe} 
                          />
                        ) : message.image_url ? (
                          <ChatImage imageUrl={message.image_url} isOwnMessage={isMe} />
                        ) : message.audio_url ? (
                          <VoiceMessagePlayer 
                            audioUrl={message.audio_url} 
                            isOwnMessage={isMe} 
                          />
                        ) : (
                          <p className="break-words">{message.content}</p>
                        )}
                        <div className={`flex items-center justify-end gap-1 mt-1 ${isMe ? "text-white/70" : "text-muted-foreground"}`}>
                          <span className="text-[10px]">
                            {new Date(message.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                          {isMe && isLastFromMe && (
                            <ReadReceipt isRead={message.is_read ?? false} />
                          )}
                        </div>
                      </div>
                      <MessageReactions 
                        reactions={reactions} 
                        onToggleReaction={(emoji) => handleToggleReaction(message.id, emoji)}
                        isOwnMessage={isMe}
                      />
                    </div>
                    <ReactionPicker 
                      onSelectReaction={(emoji) => handleToggleReaction(message.id, emoji)}
                      isOwnMessage={isMe}
                    />
                  </div>
                </div>
              );
            })}
            {isOtherUserTyping && <TypingIndicator userName={typingUserName} />}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Emoji Picker */}
      {showEmoji && (
        <div className="absolute bottom-20 left-4 right-4 bg-card border border-border rounded-2xl p-3 shadow-lg">
          <div className="flex flex-wrap gap-2">
            {EMOJI_LIST.map((emoji) => (
              <button
                key={emoji}
                onClick={() => addEmoji(emoji)}
                className="text-2xl hover:scale-125 transition-transform"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input - locked until video exchange */}
      <div className="p-4 border-t border-border bg-card">
        {!mySentVideo ? (
          // First message must be video
          <div className="flex flex-col items-center gap-2 py-2">
            <p className="text-sm text-muted-foreground">Start with a video hello!</p>
            <button
              onClick={() => setShowVideoRecorder(true)}
              className="px-6 py-3 rounded-full pop-gradient text-white font-medium inline-flex items-center gap-2"
            >
              <Video className="w-5 h-5" />
              Record Video
            </button>
          </div>
        ) : !videoExchangeComplete ? (
          // Waiting for other to send video
          <div className="flex flex-col items-center gap-2 py-2">
            <p className="text-sm text-muted-foreground">
              Waiting for {otherUser.name} to send a video...
            </p>
            <button
              onClick={() => setShowVideoRecorder(true)}
              className="text-sm text-pop hover:underline"
            >
              Send another video
            </button>
          </div>
        ) : (
          // Full messaging unlocked after video exchange
          <div className="flex items-center gap-2">
            <ImagePicker chatId={chatId} onImageSent={refetch} />
            <GifPicker onSelectGif={handleSendGif} />
            <button
              onClick={() => setShowVideoRecorder(true)}
              className="p-2 rounded-full transition-colors hover:bg-muted"
              title="Send video message"
            >
              <Video className="w-6 h-6" />
            </button>
            <button
              onClick={() => setShowEmoji(!showEmoji)}
              className={`p-2 rounded-full transition-colors ${showEmoji ? "bg-pop text-white" : "hover:bg-muted"}`}
            >
              <Smile className="w-6 h-6" />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={stopTyping}
              placeholder="Type a message..."
              className="flex-1 px-4 py-3 rounded-full bg-muted border-0 focus:ring-2 focus:ring-pop outline-none"
            />
            {newMessage.trim() ? (
              <button
                onClick={handleSend}
                disabled={isSending}
                className="p-3 rounded-full pop-gradient text-white disabled:opacity-50 transition-opacity"
              >
                <Send className="w-5 h-5" />
              </button>
            ) : (
              <VoiceRecorder 
                chatId={chatId} 
                onMessageSent={refetch} 
              />
            )}
          </div>
        )}
      </div>

      {/* Video Message Recorder */}
      {showVideoRecorder && (
        <VideoMessageRecorder
          chatId={chatId}
          onVideoSent={() => {
            setShowVideoRecorder(false);
            refetch();
          }}
          onCancel={() => setShowVideoRecorder(false)}
          isFirstMessage={messages.length === 0}
        />
      )}
    </div>
  );
});

ChatModal.displayName = "ChatModal";

export default ChatModal;
